/* eslint-disable indent */
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';
import { createSpecializeReviewValidationSchema } from 'features/SpecializeReview/validation';
import { CreateForm } from 'features/SpecializeReview/components';
import {
  create as createSpecializeReview,
  getDetail,
  getGeneralProjectProposalInfo,
} from 'apis/specializeReviews.api';
import { toast } from 'layout';
import { TOAST } from 'constant';
import DynamicSpecializationForm from 'features/SpecializeReview/components/DynamicSpecializationForm';
import { is1593 } from 'utils/func';

export default function SpecializeReviewFormCreate() {
  const { t } = useTranslation();
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { fromInternal } = location.state || { fromInternal: false };
  const { projectProposalId, councilId } = useParams();
  const [searchParams] = useSearchParams();
  const councilMemberId = searchParams.get('councilMemberId');
  const userId = searchParams.get('userId');

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange', resolver: yupResolver(createSpecializeReviewValidationSchema) });

  const { isDirty, dirtyFields } = useFormState({ control });

  const { data: dataGeneralProjectProposalInfo, isLoading: isLoadingProjectProposal } = useQuery(
    ['specialize-review', 'project-proposal', councilId, projectProposalId],
    ({ queryKey: [, , _councilId, _projectProposalId] }) =>
      getGeneralProjectProposalInfo(_councilId, _projectProposalId),
    { enabled: !!projectProposalId }
  );

  const { data: dataSpecializeReview, isLoading: isLoadingSpecializeReview } = useQuery(
    ['specialize-review', 'detail', councilMemberId, councilId, projectProposalId],
    ({ queryKey: [, , _councilMemberId, _councilId, _projectProposalId] }) =>
      getDetail(_councilMemberId, _councilId, _projectProposalId),
    {
      enabled: !fromInternal,
    }
  );

  const {
    mutate,
    error: errorRes,
    isError: isErrorRes,
    isLoading: isLoadingCreate,
  } = useMutation(createSpecializeReview);

  const onSubmit = async (data) => {
    const reviewExpenditure = Object.keys(data?.expenditure)?.reduce((newArr, key) => {
      newArr.push({ expenditureTypeId: key, ...data?.expenditure?.[key] });
      return newArr;
    }, []);

    const submitData = {
      projectProposalId,
      councilId,
      councilMemberId,
      reviewImportance: data?.reviewImportance,
      reviewQuality: data?.reviewQuality,
      reviewPicapability: data?.reviewPicapability,
      reviewConclusion: data?.reviewConclusion,
      reviewExpenditure: JSON.stringify(reviewExpenditure),
      expenditureTotalSuggestion: data?.expenditureTotalSuggestion,

      scoreCreativity: data?.score?.scoreCreativity ?? 0,
      scoreSuitableness: data?.score?.scoreSuitableness ?? 0,
      scoreImportance:
        data?.score?.scoreImportance ??
        (data?.score?.scoreCreativity ?? 0) + (data?.score?.scoreSuitableness ?? 0),
      scoreQuality: data?.score?.scoreQuality ?? 0,
      scoreAnnouncement: data?.score?.scoreAnnouncement ?? 0,
      scoreTraining: data?.score?.scoreTraining ?? 0,
      scoreProduct:
        data?.score?.scoreProduct ??
        (data?.score?.scoreAnnouncement ?? 0) + (data?.score?.scoreTraining ?? 0),
      scoreCapability: data?.score?.scoreCapability ?? 0,
      scoreSubCriteria: data?.score?.scoreSubCriteria ?? 0,
    };

    mutate(submitData, {
      onSuccess: () => {
        reset({}, { keepValues: true });
        toast(TOAST.SUCCESS, t('council.review.createSuccess'));
        setTimeout(() => {
          const queryParams = `councilMemberId=${councilMemberId}&userId=${userId}`;
          navigate(
            `/projects-revise/${projectProposalId}/council/${councilId}/specialize-review/edit?${queryParams}`,
            { replace: true }
          );
        }, 500);
      },
      onError: () => {
        setIsVisibleDialogError(true);
      },
    });
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  const template = useMemo(
    () =>
      dataGeneralProjectProposalInfo?.data?.template ||
      parseInt(dataGeneralProjectProposalInfo?.data?.template, 10) ||
      null,
    [dataGeneralProjectProposalInfo]
  );

  const renderForm = () => {
    if (is1593(template) || isLoadingProjectProposal) {
      return (
        <DynamicSpecializationForm
          projectProposalId={projectProposalId}
          councilMemberId={councilMemberId}
          councilId={councilId}
          generalInfo={dataGeneralProjectProposalInfo?.data}
          isLoading={isLoadingProjectProposal}
        />
      );
    }
    return (
      <CreateForm
        isLoadingCreate={isLoadingCreate || isLoadingProjectProposal}
        isErrorRes={isErrorRes}
        errorRes={errorRes}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        control={control}
        watch={watch}
        setValue={setValue}
        getValues={getValues}
        isDirty={isDirty}
        dirtyFields={dirtyFields}
        errors={errors}
        isValid={isValid}
        isVisibleErrorDialog={isVisibleErrorDialog}
        setIsVisibleDialogError={setIsVisibleDialogError}
        dataGeneralProjectProposalInfo={dataGeneralProjectProposalInfo}
        projectProposalIdV2={projectProposalId}
      />
    );
  };

  if (!isLoadingSpecializeReview && dataSpecializeReview?.data?.specializeReview) {
    const queryParams = `councilMemberId=${councilMemberId}&userId=${userId}`;
    navigate(
      `/projects-revise/${projectProposalId}/council/${councilId}/specialize-review/edit?${queryParams}`,
      { replace: true }
    );
  }
  // #endregion

  return (
    <>
      <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields).length}>
        {({ isActive, onConfirm, onCancel }) => (
          <Dialog
            visible={isActive}
            header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
            position="center"
            style={{ width: '300px' }}
            onHide={onCancel}
            draggable={false}
            footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
          >
            <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
          </Dialog>
        )}
      </ReactRouterPrompt>
      {renderForm()}
    </>
  );
}
